import { t } from "i18next";
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { Calendar } from "iconsax-react";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBSelect from "../../component/global/DBSelect";
import DBSwitch from "../../component/global/DBSwitch";
import DatePickerModal from "../../component/global/DatePicker";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    FEEDBACK_AI_SUMMARY_URL,
    FEEDBACK_AI_SUMMARY_USERS_URL,
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_TREE,
} from "../../helperData/ApiAddress";
import { messageStatusFilterOptions } from "./messageStatus";
import {
    flattenItemTypeOptions,
    toAiSummaryParams,
} from "./aiSummaryFilters";
import { AiCoinHint } from "../../features/ai-billing/AiCoinGate";

const toSelectOptions = (rows) =>
    (Array.isArray(rows) ? rows : rows?.results || []).map((row) => ({
        value: row.id,
        label: row.name,
    }));

export default function MessagesAiSummaryModal({
    show,
    filters,
    companyId,
    generating,
    onHide,
    onApply,
    onGenerate,
}) {
    const [draft, setDraft] = useState(filters);
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [matchedCount, setMatchedCount] = useState(null);
    const [startDatePicker, setStartDatePicker] = useState(false);
    const [endDatePicker, setEndDatePicker] = useState(false);

    const [getItemTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getUsers] = useRequest(FEEDBACK_AI_SUMMARY_USERS_URL, "GET");
    const [getPreview, previewLoading] = useRequest(
        FEEDBACK_AI_SUMMARY_URL,
        "GET"
    );

    useEffect(() => {
        if (!show) return;
        setDraft(filters);
        setMatchedCount(null);
        getItemTypes(undefined, undefined, companyId || undefined)
            .then((tree) =>
                setItemTypeOptions(
                    flattenItemTypeOptions(
                        Array.isArray(tree) ? tree : tree?.children || []
                    )
                )
            )
            .catch(() => setItemTypeOptions([]));
        getUsers(toAiSummaryParams(filters, companyId))
            .then((rows) => setUsers(Array.isArray(rows) ? rows : []))
            .catch(() => setUsers([]));
    }, [show]);

    useEffect(() => {
        if (!show || !draft.item_type) {
            setItems([]);
            return undefined;
        }
        let cancelled = false;
        getItems({
            item_type: draft.item_type,
            company: companyId,
        })
            .then((r) => {
                if (!cancelled) setItems(toSelectOptions(r));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            });
        return () => {
            cancelled = true;
        };
    }, [show, draft.item_type]);

    useEffect(() => {
        if (!show) return undefined;
        const timer = setTimeout(() => {
            getPreview(toAiSummaryParams(draft, companyId))
                .then((r) => setMatchedCount(r.matched_count ?? 0))
                .catch(() => setMatchedCount(null));
        }, 300);
        return () => clearTimeout(timer);
    }, [show, draft, companyId]);

    const handleItemTypeChange = (value) => {
        setDraft({
            ...draft,
            item_type: value ?? null,
            item: null,
        });
    };

    const stampLabels = (next) => ({
        ...next,
        itemTypeLabel:
            itemTypeOptions.find((row) => row.value === next.item_type)
                ?.label || "",
        itemLabel:
            items.find((row) => row.value === next.item)?.label || "",
        userLabel: users.find((row) => row.value === next.user)?.label || "",
    });

    const statusOptions = messageStatusFilterOptions(t);

    return (
        <DBModal
            permanent
            show={show}
            minWidth={720}
            onHide={onHide}
            className="messages-ai-summary-modal survey-form-modal"
            ariaLabelledby="messages-ai-summary-modal-title"
        >
            <DBModal.Header
                closeBtn={onHide}
                closeLabel={t("Organizational.Messages.AiSummary.Close")}
                title={
                    <span id="messages-ai-summary-modal-title">
                        {t("Organizational.Messages.AiSummary.Settings")}
                    </span>
                }
            />
            <DBModal.Content>
                <p className="survey-form-help">
                    {t("Organizational.Messages.AiSummary.Intro")}
                </p>
                <div className="survey-form-grid">
                    <DBFieldBlock
                        title={t("Organizational.Messages.AiSummary.ItemType")}
                    >
                        <DBSelect
                            size="large"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder={t("form.select")}
                            value={draft.item_type ?? undefined}
                            onChange={handleItemTypeChange}
                            options={itemTypeOptions}
                        />
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("Organizational.Messages.AiSummary.Item")}
                    >
                        <DBSelect
                            size="large"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder={t("form.select")}
                            value={draft.item ?? undefined}
                            onChange={(value) =>
                                setDraft({ ...draft, item: value ?? null })
                            }
                            options={items}
                            disabled={!draft.item_type}
                        />
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("Organizational.Messages.AiSummary.FromDate")}
                    >
                        <button
                            type="button"
                            className="messages-ai-date-trigger"
                            onClick={() => setStartDatePicker(true)}
                        >
                            <Calendar size={16} aria-hidden="true" />
                            <span>
                                {draft.datetime_from.fa ||
                                    t("Organizational.Messages.AiSummary.PickDate")}
                            </span>
                        </button>
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("Organizational.Messages.AiSummary.ToDate")}
                    >
                        <button
                            type="button"
                            className="messages-ai-date-trigger"
                            onClick={() => setEndDatePicker(true)}
                        >
                            <Calendar size={16} aria-hidden="true" />
                            <span>
                                {draft.datetime_to.fa ||
                                    t("Organizational.Messages.AiSummary.PickDate")}
                            </span>
                        </button>
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("Organizational.Messages.AiSummary.User")}
                    >
                        <DBSelect
                            size="large"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder={t("form.select")}
                            value={draft.user ?? undefined}
                            onChange={(value) =>
                                setDraft({ ...draft, user: value ?? null })
                            }
                            options={users}
                        />
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("Organizational.Messages.MessagesStatus")}
                    >
                        <DBSelect
                            size="large"
                            value={draft.feedback_status || ""}
                            onChange={(value) =>
                                setDraft({
                                    ...draft,
                                    feedback_status: value || "",
                                })
                            }
                            options={statusOptions}
                        />
                    </DBFieldBlock>
                </div>
                <div className="survey-form-toggles">
                    <label>
                        <DBSwitch
                            checked={draft.include_comments}
                            onChange={(value) =>
                                setDraft({
                                    ...draft,
                                    include_comments: value,
                                })
                            }
                        />
                        <span>
                            {t("Organizational.Messages.AiSummary.IncludeComments")}
                        </span>
                    </label>
                </div>
                <p
                    className="messages-ai-summary-count"
                    aria-live="polite"
                >
                    {previewLoading && matchedCount == null
                        ? t("Organizational.Messages.AiSummary.Counting")
                        : matchedCount == null
                        ? ""
                        : t("Organizational.Messages.AiSummary.Matched", {
                              count: matchedCount,
                          })}
                </p>
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={
                        <>
                            {t("Organizational.Messages.AiSummary.Generate")}
                            <AiCoinHint operation="message_summary" />
                        </>
                    }
                    loading={generating}
                    disabled={matchedCount === 0}
                    onClick={() => onGenerate(stampLabels(draft))}
                />
                <DBButton
                    type="secondary"
                    caption={t("Organizational.Messages.AiSummary.Save")}
                    onClick={() => onApply(stampLabels(draft))}
                />
            </DBModal.Footer>
            {createPortal(
                <>
                    <DatePickerModal
                        show={startDatePicker}
                        close={setStartDatePicker}
                        defaultValue={draft.datetime_from.fa}
                        className="date-picker--stacked"
                        onChange={(value) =>
                            setDraft({ ...draft, datetime_from: value })
                        }
                    />
                    <DatePickerModal
                        show={endDatePicker}
                        close={setEndDatePicker}
                        defaultValue={draft.datetime_to.fa}
                        className="date-picker--stacked"
                        onChange={(value) =>
                            setDraft({ ...draft, datetime_to: value })
                        }
                    />
                </>,
                document.body
            )}
        </DBModal>
    );
}
